import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loading } from '../../components/office';
import { purchaseOrdersApi, suppliersApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Checkbox } from '@verlo/shared/components/ui/checkbox';
import { Input } from '@verlo/shared/components/ui/input';
import { Notice } from '@verlo/shared/components/ui/notice';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@verlo/shared/components/ui/select';
import { cutOffWords, deliveryWords } from '../../lib/delivery';
import { qty, theirs } from '../../lib/orders';
import OrderEmailField, { looksLikeEmail } from './OrderEmailField';

// Review and send (Oct 2026, mock "Sending orders"). The drafts on one screen, each ticked to
// go or not (Martin, 27 Oct: pick which of them go): what's on it, with the quantities still
// changeable here (down to nothing takes a line off); who it's emailed to; the delivery it
// makes on the supplier's days and cut-off; and whether it's under their minimum (it can
// still go: Martin, 26 Oct). One button emails the ticked ones. A supplier with no email for
// orders gets one typed here, saved to their account or used just this once, or the order is
// marked as sent without emailing (phoned through, their website), which can be undone.
// Anything not sent stays a draft on Orders.

const money = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v || 0);
const round2 = (n) => Math.round(n * 100) / 100;
const totalOf = (lines) => round2(lines.reduce((t, l) => t + l.quantity * l.price, 0));
const BOX = 'border-hairline-strong data-[state=checked]:border-verlo data-[state=checked]:bg-verlo data-[state=checked]:text-white';

export default function ReviewSendPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const ids = useMemo(() => (params.get('ids') || '').split(',').filter(Boolean), [params]);
  const [plan, setPlan] = useState(null);
  const [via, setVia] = useState(null);
  const [when, setWhen] = useState({});        // order id → delivery date chosen
  const [picked, setPicked] = useState({});    // order id → ticked to go now
  const [emails, setEmails] = useState({});    // supplier id → { value, keep } typed for one with no email
  const [byHand, setByHand] = useState({});    // order id → marked sent by hand here
  const [failed, setFailed] = useState({});    // order id → why it didn't go
  const [busy, setBusy] = useState(false);
  const saves = useRef({});                    // order id → its last save, so they land in order

  const load = () => purchaseOrdersApi.sendPlan(ids).then(({ data }) => {
    const drafts = data.orders.filter((o) => o.status === 'draft');
    setPlan(drafts);
    setVia(data.email_via);
    setWhen((cur) => Object.fromEntries(drafts.map((o) => [o.id, cur[o.id] ?? o.deliver_on ?? o.deliveries[0]?.date ?? ''])));
    setPicked((cur) => Object.fromEntries(drafts.map((o) => [o.id, cur[o.id] ?? o.lines.length > 0])));
  }).catch(() => toast.error("Couldn't load the orders"));
  useEffect(() => { if (ids.length) load(); else setPlan([]); }, [ids.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!plan) return <Loading />;

  const emailFor = (o) => emails[o.supplier.id] || { value: '', keep: true };
  const typed = (o) => (!o.can_email && looksLikeEmail(emailFor(o).value) ? emailFor(o).value.trim() : null);
  const underMinimum = (o) => (o.supplier.minimum_order > 0 && o.total < o.supplier.minimum_order ? o.supplier.minimum_order : null);
  const ready = plan.filter((o) => picked[o.id] && o.lines.length && !byHand[o.id] && via && (o.can_email || typed(o)));
  const total = ready.reduce((t, o) => t + o.total, 0);
  const ticked = plan.filter((o) => picked[o.id] && !byHand[o.id]);
  const needEmail = ticked.filter((o) => !o.can_email && !typed(o)).length;
  const allTicked = plan.every((o) => byHand[o.id] || !o.lines.length || picked[o.id]);

  // A quantity changed here saves straight to the draft, one save after another per order.
  const save = (o, lines) => {
    const run = (saves.current[o.id] || Promise.resolve()).then(() => purchaseOrdersApi.update(o.id, {
      lines: lines.map((l) => ({ stock_id: l.stock_id, quantity: l.quantity, price: l.price })),
      deliver_on: when[o.id] || null, note: o.note || '',
    })).catch((e) => { toast.error(e?.response?.data?.detail || "Couldn't save the change"); load(); });
    saves.current[o.id] = run;
    return run;
  };
  const step = (o, l, dir) => {
    const next = Math.round((l.quantity + dir * (Number(l.pack_size) || 1)) * 1000) / 1000;
    const lines = next > 0 ? o.lines.map((x) => (x.stock_id === l.stock_id ? { ...x, quantity: next } : x)) : o.lines.filter((x) => x.stock_id !== l.stock_id);
    setPlan((p) => p.map((x) => (x.id === o.id ? { ...x, lines, total: totalOf(lines) } : x)));
    if (!lines.length) setPicked((p) => ({ ...p, [o.id]: false }));
    save(o, lines);
  };

  // Marked as sent without emailing; Undo puts it back to a draft.
  const undoByHand = async (o) => {
    try {
      await purchaseOrdersApi.unsend(o.id);
      setByHand((b) => { const { [o.id]: _, ...rest } = b; return rest; });
      toast.success(`${o.number} is a draft again`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't undo it"); }
  };
  const sendMyself = async (o) => {
    try {
      await purchaseOrdersApi.markSent(o.id);
      setByHand((b) => ({ ...b, [o.id]: true }));
      toast.success(`${o.number} marked as sent, not emailed`, { action: { label: 'Undo', onClick: () => undoByHand(o) } });
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't mark it sent"); }
  };
  const sendAll = async () => {
    setBusy(true);
    try {
      await Promise.all(Object.values(saves.current));
      // An address typed and kept goes on the supplier first (once each), so it's theirs from now on.
      const keepers = [...new Map(ready.filter((o) => typed(o) && emailFor(o).keep).map((o) => [o.supplier.id, o])).values()];
      for (const o of keepers) await suppliersApi.patch(o.supplier.id, { email: typed(o) });
      const { data } = await purchaseOrdersApi.sendMany(ready.map((o) => ({
        id: o.id, deliver_on: when[o.id] || null, ...(typed(o) && !emailFor(o).keep ? { to: typed(o) } : {}),
      })));
      const bad = data.results.filter((r) => !r.ok);
      if (bad.length) {
        setFailed(Object.fromEntries(bad.map((r) => [r.id, r.detail])));
        toast.error(`${bad.length} didn't go: see below. The rest were emailed.`);
        await load();
        return;
      }
      navigate('/back-office/orders', { state: { sent: {
        emailed: data.sent, byHand: Object.keys(byHand).length,
        // Not ticked, or with nowhere to email it: still drafts on Orders.
        drafts: plan.filter((o) => !byHand[o.id] && !ready.includes(o)).map((o) => o.number),
        total: data.results.reduce((t, r) => t + (r.total || 0), 0), via,
      } } });
      toast.success(`${data.sent} order${data.sent === 1 ? '' : 's'} emailed`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send them"); } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-4" data-testid="review-send">
      <Link to="/back-office/orders" className="text-[13px] text-ink-soft hover:text-ink">‹ Orders</Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-[26px] font-extrabold text-ink">Review and send</h1>
        <span className="rounded-md border border-hairline bg-white px-2.5 py-1 text-[13px] text-ink-soft">{plan.length} order{plan.length === 1 ? '' : 's'} · {money(plan.reduce((t, o) => t + o.total, 0))}</span>
        {plan.length > 1 && (
          <button type="button" className="ml-auto text-[13px] font-semibold text-verlo hover:underline" data-testid="review-send-tick-all"
            onClick={() => setPicked(Object.fromEntries(plan.map((o) => [o.id, !allTicked && o.lines.length > 0])))}>
            {allTicked ? 'Untick all' : 'Tick all'}
          </button>
        )}
      </div>

      {via === 'dev_mailbox' && (
        <Notice tone="info" word="Dev stack" data-testid="review-send-dev">
          Nothing leaves this machine: each email is kept in the mailbox at <Link className="text-verlo hover:underline" to="/back-office/orders/emails">Emails sent</Link>, exactly as the supplier would get it.
        </Notice>
      )}
      {!via && (
        <Notice tone="warn" word="Email" data-testid="review-send-no-email">
          Email isn&apos;t set up here, so nothing can be emailed. Send each order yourself and mark it sent.
        </Notice>
      )}
      {!plan.length && (
        <Notice tone="info" word="Orders">Nothing here is still to send. <Link className="text-verlo hover:underline" to="/back-office/orders">Back to Orders</Link></Notice>
      )}

      {plan.map((o) => {
        const chosen = when[o.id];
        const known = o.deliveries.find((d) => d.date === chosen);
        const done = byHand[o.id];
        const on = picked[o.id] && !done;
        const minimum = underMinimum(o);
        const email = emailFor(o);
        return (
          <section key={o.id} className={`grid gap-0 rounded-lg border bg-white lg:grid-cols-[minmax(0,1fr)_260px] ${minimum && on ? 'border-signal-warn-line' : 'border-hairline'} ${on ? '' : 'bg-surface-2/60'}`} data-testid={`send-card-${o.number}`}>
            <div className="min-w-0 space-y-2 border-b border-hairline p-4 lg:border-b-0 lg:border-r">
              <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                {!done && (
                  <Checkbox checked={!!picked[o.id]} disabled={!o.lines.length} onCheckedChange={(c) => setPicked((p) => ({ ...p, [o.id]: c === true }))}
                    aria-label={`Send ${o.number} to ${o.supplier.name}`} className={`h-5 w-5 ${BOX}`} data-testid={`send-pick-${o.number}`} />
                )}
                <h2 className={`font-heading text-[16px] font-extrabold ${on ? 'text-ink' : 'text-ink-soft'}`}>{o.supplier.name}</h2>
                <span className="font-mono text-[12px] text-ink-faint">{o.number}</span>
                {done && <Signal tone="ok" dot={false}>marked as sent, not emailed</Signal>}
                {done && <button type="button" className="text-[12.5px] font-semibold text-verlo hover:underline" onClick={() => undoByHand(o)} data-testid={`send-undo-${o.number}`}>Undo</button>}
                {!done && !picked[o.id] && o.lines.length > 0 && <Signal tone="info" dot={false}>staying a draft</Signal>}
              </div>

              {o.lines.length ? (
                <ul className="divide-y divide-hairline rounded-md border border-hairline" data-testid={`send-lines-${o.number}`}>
                  {o.lines.map((l) => (
                    <li key={l.stock_id} className="grid grid-cols-[minmax(0,1fr)_auto_72px] items-center gap-3 px-3 py-1.5" data-testid={`send-line-${l.stock_id}`}>
                      <span className="min-w-0 truncate text-[13.5px] text-ink" title={l.name}>{l.name}{l.order_code && <span className="ml-2 font-mono text-[11.5px] text-ink-faint">{l.order_code}</span>}</span>
                      {done ? <span className="font-mono text-[13px] tabular-nums text-ink-soft">{qty(l.quantity, l.unit)}</span> : (
                        <span className="flex h-8 items-center overflow-hidden rounded-md border border-hairline-strong bg-white">
                          <button type="button" className="h-full w-7 bg-hover-tint font-bold text-verlo" onClick={() => step(o, l, -1)} aria-label={`Less ${l.name}`}>−</button>
                          <span className="min-w-[64px] px-1 text-center font-mono text-[12.5px] tabular-nums">{qty(l.quantity, l.unit)}</span>
                          <button type="button" className="h-full w-7 bg-hover-tint font-bold text-verlo" onClick={() => step(o, l, 1)} aria-label={`More ${l.name}`}>+</button>
                        </span>
                      )}
                      <span className="text-right font-mono text-[13px] tabular-nums text-ink">{money(l.quantity * l.price)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[13px] text-ink-soft">Nothing on it now, so it stays a draft. Add to it from <Link className="text-verlo hover:underline" to={`/back-office/orders/${o.id}`}>{o.number}</Link>.</p>
              )}

              <p className="text-[13px] text-ink-soft">
                {o.lines.length} item{o.lines.length === 1 ? '' : 's'} · <span className="font-mono font-semibold text-ink">{money(o.total)}</span>
                {o.can_email && <> · to {o.supplier.email}{o.supplier.order_cc ? `, copy to ${o.supplier.order_cc}` : ''}</>}
                {o.supplier.account_number && <> · account {o.supplier.account_number}</>}
              </p>

              {!o.can_email && on && (
                <div className="space-y-1.5 pt-1" data-testid={`send-no-email-${o.number}`}>
                  <p className="text-[13px] text-signal-warn">{o.supplier.name} has no email for orders yet. Where should it go?</p>
                  <OrderEmailField supplierName={o.supplier.name} value={email.value} keep={email.keep} testId={`send-email-${o.number}`} className="max-w-sm"
                    onChange={(value) => setEmails((e) => ({ ...e, [o.supplier.id]: { ...email, value } }))}
                    onKeep={(keep) => setEmails((e) => ({ ...e, [o.supplier.id]: { ...email, keep } }))} />
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1">
                    <Button size="sm" variant="outline" onClick={() => sendMyself(o)} data-testid={`send-myself-${o.number}`}>Mark as sent without emailing</Button>
                    <span className="text-[12px] text-ink-faint">For an order you&apos;ll phone through or place on their website.</span>
                  </div>
                </div>
              )}

              {minimum && on && (
                <Notice tone="warn" word="Minimum" data-testid={`send-minimum-${o.number}`}>
                  Under {theirs(o.supplier.name)} {money(minimum)} minimum order. You can still send it; they may add a charge or ask you to top it up.
                </Notice>
              )}
              {failed[o.id] && <p className="text-[13px] font-medium text-signal-stop">Didn&apos;t go: {failed[o.id]}</p>}
            </div>

            <div className="space-y-1.5 p-4">
              <p className="text-[12px] text-ink-soft">Delivery</p>
              {o.deliveries.length ? (
                <Select value={chosen || ''} onValueChange={(v) => setWhen({ ...when, [o.id]: v })} disabled={!on}>
                  <SelectTrigger className="h-9 bg-white" data-testid={`send-delivery-${o.number}`}><SelectValue placeholder="Pick a day" /></SelectTrigger>
                  <SelectContent>{o.deliveries.map((d) => <SelectItem key={d.date} value={d.date}>{deliveryWords(d.date)}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input type="date" className="h-9 bg-white" value={chosen || ''} onChange={(e) => setWhen({ ...when, [o.id]: e.target.value })} disabled={!on} aria-label={`Delivery for ${o.supplier.name}`} />
              )}
              <p className="text-[12px] text-ink-faint">
                {known?.order_by ? cutOffWords(known.order_by) : o.deliveries.length ? '' : `Set ${theirs(o.supplier.name)} delivery days on Suppliers and Verlo works this out.`}
              </p>
            </div>
          </section>
        );
      })}

      {plan.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-hairline-strong pt-4">
          <p className="text-[13px] text-ink-soft" data-testid="review-send-summary">
            {ticked.length} of {plan.length} ticked{needEmail ? ` · ${needEmail} need${needEmail === 1 ? 's' : ''} an email` : ''}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/back-office/orders')}>Save for later</Button>
            <Button className="bg-verlo hover:bg-verlo-hover" disabled={!ready.length || busy} onClick={sendAll} data-testid="review-send-go">
              {busy ? 'Sending…' : `Email ${ready.length} order${ready.length === 1 ? '' : 's'} · ${money(total)}`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
